/**
 * Impact-rating criteria lookup.
 *
 * Customers could see that an impact was rated "Severe" but not what that means
 * or why it is defensible. The criteria already exist in the tenant's own
 * configuration — one definition per (category × level) carrying the qualifying
 * `criteria` text, `examples`, and (for Financial) a `monetary_range`.
 *
 * Fetched once per mount and exposed as a lookup, so the rating badges can
 * explain themselves from the customer's configured methodology rather than
 * from model prose. Fetch failures degrade to "no explanation available" — the
 * ratings still render.
 */
import { useEffect, useState, useCallback } from 'react';
import { getImpactRatingConfiguration } from '../../../api/configuration';

export interface ImpactRatingDefinition {
  rating_level: string;
  category: string;
  criteria: string;
  examples?: string[];
  monetary_range?: string | null;
}

/** `${category}|${level}`, both lowercased so lookups are case-insensitive. */
const key = (category: string, level: string) =>
  `${String(category).toLowerCase()}|${String(level).toLowerCase()}`;

export function useImpactCriteria() {
  const [byKey, setByKey] = useState<Record<string, ImpactRatingDefinition>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getImpactRatingConfiguration();
        const defs: ImpactRatingDefinition[] =
          res?.configuration?.definitions ?? res?.definitions ?? [];
        if (cancelled || !Array.isArray(defs)) return;
        const map: Record<string, ImpactRatingDefinition> = {};
        for (const d of defs) {
          if (d?.category && d?.rating_level) map[key(d.category, d.rating_level)] = d;
        }
        setByKey(map);
      } catch {
        // Non-fatal: the ratings still render, just without their criteria.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** One-line explanation for a rating, or '' when we have nothing to say. */
  const describe = useCallback(
    (category: string, level: unknown): string => {
      if (!level) return '';
      const def = byKey[key(category, String(level))];
      if (!def) return '';
      const parts = [`${def.category} — ${def.rating_level}: ${def.criteria}`];
      if (def.monetary_range) parts.push(`Range: ${def.monetary_range}`);
      if (def.examples?.length) parts.push(`e.g. ${def.examples[0]}`);
      return parts.join('\n\n');
    },
    [byKey],
  );

  return { describe, loaded: Object.keys(byKey).length > 0 };
}
